import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import Chart from "react-apexcharts";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import {ProjectStatisticRepository} from "../../../../repositories/ProjectStatisticRepository";
import {Panel, PanelHeading} from "./Panel";
import {csvFileName, exportCsv, ExportButton} from "./ExportButton";
import {DateRangePickerMenu} from "./DateRangePickerMenu";
import {QUICK_RANGES} from "../../../../components/date-range/QuickRangeMenu";

// Two series on one axis (same unit: calls); pair validated for colour-blind separation
const CALLS_COLOR = "#6C78E8";
const REDIRECTED_COLOR = "#2FA3B5";

const defaultRange = () => {
  const last7 = QUICK_RANGES.find(item => item.label === "Last 7 days");
  const [start, end] = last7.calc();
  return {label: "Last 7 days", start, end};
};

const LegendItem = ({color, label, dashed}) => (
  <Stack direction={"row"} alignItems={"center"} gap={0.75}>
    <Box sx={{width: 14, height: 0, borderTop: `2.5px ${dashed ? "dashed" : "solid"} ${color}`}}/>
    <Typography sx={{fontSize: 12, color: "#6B6B80"}}>{label}</Typography>
  </Stack>
);

const Stat = ({label, value}) => (
  <Box>
    <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{label}</Typography>
    <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>{value}</Typography>
  </Box>
);

// Conversations created per day and how many of them were redirected (project statistic /daily-calls)
export const ActivityPanel = ({projectId, filters, refreshInterval, conversationLabel = "Conversations"}) => {
  const [range, setRange] = useState(defaultRange);
  const [statistic, setStatistic] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!projectId || !range?.start || !range?.end) return;
    const requestId = ++requestRef.current;
    try {
      const response = await ProjectStatisticRepository.getDailyCalls({
        projectId,
        filters,
        start: moment(range.start).format("YYYY-MM-DD"),
        end: moment(range.end).format("YYYY-MM-DD"),
      });
      if (requestId === requestRef.current) setStatistic(response?.data || {items: []});
    } catch (e) {
      console.error("Error fetching daily calls:", e);
      if (requestId === requestRef.current) setStatistic(prev => prev || {items: []});
    }
  }, [projectId, filters, range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshInterval) return;
    const intervalId = setInterval(load, refreshInterval);
    return () => clearInterval(intervalId);
  }, [load, refreshInterval]);

  const items = useMemo(() => statistic?.items || [], [statistic]);
  const calls = Number(statistic?.calls) || 0;
  const redirected = Number(statistic?.redirected) || 0;

  // One category per day: the axis label sits exactly on the day's point, where the hover crosshair snaps
  // (a datetime axis places its labels on its own tick grid, off the points)
  const categories = useMemo(() => items.map(item => moment(item.date).format("DD MMM")), [items]);
  const series = useMemo(() => [
    {name: conversationLabel, type: "area", data: items.map(item => item.calls)},
    {name: "Redirected", type: "line", data: items.map(item => item.redirected)},
  ], [items, conversationLabel]);

  const options = {
    chart: {
      type: "line",
      toolbar: {show: false},
      zoom: {enabled: false},
      animations: {enabled: false},
      fontFamily: "Inter, sans-serif",
    },
    colors: [CALLS_COLOR, REDIRECTED_COLOR],
    stroke: {curve: "smooth", width: [2.5, 2], dashArray: [0, 5]},
    fill: {
      type: ["gradient", "solid"],
      opacity: [1, 1],
      gradient: {shadeIntensity: 0, opacityFrom: 0.24, opacityTo: 0, stops: [0, 100]},
    },
    markers: {size: 0, hover: {size: 5}, strokeColors: "#fff", strokeWidth: 2},
    dataLabels: {enabled: false},
    legend: {show: false},
    grid: {borderColor: "#F0F0F6", strokeDashArray: 4, padding: {left: 8, right: 8}},
    xaxis: {
      type: "category",
      categories,
      tickPlacement: "on",
      // every day for a week, about 8 evenly spaced labels for longer ranges
      tickAmount: items.length > 10 ? 8 : undefined,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: {colors: "#A3A3B5", fontSize: "11px"},
      },
      axisBorder: {show: false},
      axisTicks: {show: false},
      crosshairs: {stroke: {color: "#D6D6E4", dashArray: 0}},
      tooltip: {enabled: false},
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      tickAmount: 4,
      labels: {style: {colors: "#A3A3B5", fontSize: "11px"}, formatter: (value) => Math.round(value).toLocaleString()},
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {formatter: (value, {dataPointIndex} = {}) => {
        const item = items[dataPointIndex];
        return item ? moment(item.date).format("ddd, DD MMM YYYY") : value;
      }},
      y: {formatter: (value) => (Number(value) || 0).toLocaleString()},
    },
  };

  return (
    <Panel id={"activity"} sx={{p: 2.5}}>
      <PanelHeading
        kicker={"CALL ANALYTICS"}
        title={"Conversation activity"}
        description={"How your agent is performing over time"}
        actions={<>
          <ExportButton
            disabled={!items.length}
            onClick={() => exportCsv(
              [["Date", conversationLabel, "Redirected"], ...items.map(item => [item.date, item.calls, item.redirected])],
              csvFileName(`conversation-activity-${range.label.toLowerCase().replace(/\s+/g, "-")}`)
            )}
          />
          <DateRangePickerMenu
            value={range}
            onChange={(next) => setRange(next || defaultRange())}
            allowClear={false}
            ariaLabel={"Activity range"}
          />
        </>}
      />

      <Stack direction={"row"} alignItems={"flex-end"} justifyContent={"space-between"} flexWrap={"wrap"} gap={2}
             sx={{mt: 2}}>
        <Stack direction={"row"} gap={3}>
          <Stat label={conversationLabel} value={statistic ? calls.toLocaleString() : "–"}/>
          <Stat label={"Redirected"} value={statistic ? redirected.toLocaleString() : "–"}/>
          <Stat label={"Redirect rate"} value={statistic ? (calls ? `${Math.round((redirected / calls) * 100)}%` : "0%") : "–"}/>
        </Stack>
        <Stack direction={"row"} gap={2}>
          <LegendItem color={CALLS_COLOR} label={conversationLabel}/>
          <LegendItem color={REDIRECTED_COLOR} label={"Redirected"} dashed/>
        </Stack>
      </Stack>

      <Box sx={{flex: 1, minHeight: 260, mt: 1, mx: -1}}>
        {statistic === null
          ? <Skeleton variant={"rounded"} height={250} sx={{mx: 1, mt: 1}}/>
          : <Chart options={options} series={series} type={"line"} height={260} width={"100%"}/>}
      </Box>

      <Typography sx={{mt: 0.5, fontSize: 12, color: "#8A8AA0"}}>
        A call counts as redirected when it was transferred, including transfers nobody answered.
      </Typography>
    </Panel>
  );
};
